import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApps } from "../../../hooks/useData";
import { Button, Form } from "react-bootstrap";
import { Row, Col, Drawer, Button as AntButton, Input as AntInput, Select as AntSelect, Radio } from "antd";
import { SearchOutlined, AndroidFilled, AppleFilled } from "@ant-design/icons";
import NoDataComponent from "../../../components/noData";
import { Flipper, Flipped } from "react-flip-toolkit";
import "../../../assets/css/app/flip-animation.css";
import Api from "../../../services/api";

function Apps() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { apps, loading: appsLoading, refetch: refetchApps } = useApps();
  const [appsLoaded, setAppsLoaded] = useState(false);

  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOs, setFilterOs] = useState("");
  const [appFilter, setAppFilter] = useState([]);

  
  const [sortBy, setSortBy] = useState("default"); 
  const [sortDirection, setSortDirection] = useState("desc");  

  const { studio } = useParams();
  const [studioInfo, setStudioInfo] = useState(null);

  const originalApps = useRef([]);
  const [initialCounts, setInitialCounts] = useState({
    all: 0,
    running: 0,
    off: 0
  });
  const [drawerOpen, setDrawerOpen] = useState(false);


  
  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  // Load studio info để lấy studio data
  useEffect(() => {
    if (studio) {
      console.log('Loading studio info for studio ID:', studio);
      Api.get('/api/studios')
        .then(response => {
          console.log('Studios API response:', response?.data);
          if (response?.data?.code == 0) {
            console.log('All studios data:', response.data.data);
            const studioData = response.data.data.find(s => 
              String(s.id) === String(studio) || 
              String(s.name) === String(studio) || 
              String(s.code) === String(studio)
            );
            console.log('Found studio data:', studioData);
            setStudioInfo(studioData);
          }
        })
        .catch(error => {
          console.log('Error loading studio info:', error);
        });
    }
  }, [studio]);

  
  const sortApps = useCallback((appsToSort) => {
    if (!appsToSort) return [];

    if (sortBy === "default") {
      return appsToSort;
    }
    
    return [...appsToSort].sort((a, b) => {
      if (sortBy === "name") {
        
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return sortDirection === "asc" 
          ? nameA.localeCompare(nameB) 
          : nameB.localeCompare(nameA);
      } else {
        
        const dateA = new Date(a.release_at || 0);
        const dateB = new Date(b.release_at || 0);
        return sortDirection === "desc" 
          ? dateB - dateA 
          : dateA - dateB;
      }
    });
  }, [sortBy, sortDirection]);

  
  useEffect(() => {
    if (apps) {
      originalApps.current = apps;
      const runningItems = apps.filter((item) => item.status === "running");
      const offItems = apps.filter((item) => item.status === "off");
      
      setInitialCounts({
        all: apps.length,
        running: runningItems.length,
        off: offItems.length
      });

      let filtered = apps;
      if (filterStatus === "running") {
        filtered = runningItems;
      } else if (filterStatus === "off") {
        filtered = offItems;
      }

      if (filterName) {
        const searchTerm = filterName.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.name.toLowerCase().includes(searchTerm) ||
            item.code.toLowerCase().includes(searchTerm)
        );
      }

      if (filterOs) {
        filtered = filtered.filter((item) => item.os === filterOs);
      }

      
      filtered = sortApps(filtered);

      setAppFilter(filtered);
      updateFilteredApps();
    }
  }, [apps, filterStatus, filterName, filterOs, sortBy, sortDirection, sortApps]);


  
  useEffect(() => {
    // Chỉ load apps khi đã có studioInfo
    if (!studioInfo) return;
    
    const name = searchParams.get("name") || "";
    const status = searchParams.get("status") || "";
    const os = searchParams.get("os") || "";

    setFilterName(name);
    setFilterStatus(status);
    setFilterOs(os);

    // Backend tự động filter theo quyền user thông qua PermissionHelper
    const params = { name, status, os };

    console.log('Refetching apps with params:', params);
    refetchApps(params);
    setAppsLoaded(true);
  }, [refetchApps, searchParams, studioInfo]);

  const handleReset = () => {
    setFilterName("");
    setFilterStatus("");
    setFilterOs("");
    setSearchParams({});
    
    // Backend tự động filter theo quyền user
    const params = {};
    refetchApps(params);
  };

  const updateFilteredApps = useCallback(() => {
    if (!originalApps.current) return;

    let filtered = [...originalApps.current];
    
    
    if (filterStatus === "running") {
      filtered = filtered.filter(item => item.status === "running");
    } else if (filterStatus === "off") {
      filtered = filtered.filter(item => item.status === "off");
    }

    
    if (filterName) {
      const searchTerm = filterName.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm) ||
          item.code.toLowerCase().includes(searchTerm)
      );
    }

    
    if (filterOs) {
      filtered = filtered.filter((item) => item.os === filterOs);
    }

    
    filtered = sortApps(filtered);

    setAppFilter(filtered);
  }, [filterStatus, filterName, filterOs, sortApps]);

  
  useEffect(() => {
    updateFilteredApps();
  }, [updateFilteredApps]);

  const handleLoading = useCallback(
    debounce((newFilters = {}) => {
      const params = {
        name: newFilters.name || filterName || "",
        status: newFilters.status || filterStatus || "",
        os: newFilters.os || filterOs || "",
      };

      // Backend tự động filter theo quyền user
      setSearchParams(params);
      refetchApps(params);
    }, 500),
    [filterName, filterStatus, filterOs, studioInfo]
  );

  
  const handleSortChange = (type) => {
    if (type === "name") {
      setSortBy("name");
      setSortDirection("asc"); 
    } else if (type === "release_at") {
      setSortBy("release_at");
      setSortDirection("desc"); 
    } else if (type === "default") {
      setSortBy("default");
      setSortDirection("desc");
    }
  };


  const handleChangeName = (e) => {
    setFilterName(e);
  };

  const handleChangeStatus = (value) => {
    setFilterStatus(value);
    
    updateFilteredApps();
  };

  const handleChangeOs = (value) => {
    setFilterOs(value || "");
  };

  const selectApp = (item) => {
    // No need to set app in Redux anymore, just navigate
    navigate(`/organisations/${studio}/apps/${item.code}/${item.os}/overview`);
  };


  // Hiển thị loading khi chưa có studioInfo
  if (!studioInfo) {
    return (
      <div className="background-second-dashboard">
        <section className="main__content-dashbard">
          <div className="container-fluid">
            <div className="detail-body">
              <div className="overview_content pd-20">
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading studio information...</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Responsive styles for Apps filter */
        @media (max-width: 768px) {
          .desktop-filter { display: none !important; }
          .mobile-search-toggle { display: block !important; }
          .filter-container .sort-container {
            order: 3;
          }
          .filter-container .status-container {
            order: 2;
          }
          .status-filters {
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          .status-filters > div {
            margin-right: 0.75rem !important;
            margin-bottom: 0.5rem;
          }
          .reset-btn-mobile { display: none !important; }
          .sort-label { display: none; }
        }
        @media (min-width: 769px) { .reset-btn-mobile { display: none !important; } .mobile-search-toggle { display: none !important; } }
        @media (min-width: 769px) {
          .status-container .status-filters { justify-content: flex-end; }
        }
        .list_app {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
        }
        @media (max-width: 576px) {
          .list_app {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <div className="background-second-dashboard">
        {/* Mobile: floating search icon */}
        { !drawerOpen && (
          <div className="mobile-search-toggle" style={{
            display: 'none', position: 'fixed', right: '16px', top: '54px', zIndex: 1002
          }}>
            <AntButton type="primary" onClick={() => setDrawerOpen(true)}
              icon={<SearchOutlined />}
              style={{ borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'}} />
          </div>
        )}
        <section className="main__content-dashbard">
          <div className="container-fluid">
            <div className="detail-body">
              <div className="overview_content pd-20">
                <div className="card mb-4 desktop-filter" style={{ 
                  position: 'sticky', 
                  top: '46px', 
                  zIndex: 9,
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}>
                  <div className="card-body" style={{ padding: "1.25rem" }}>
                    <div className="filter-container">
                      <Row gutter={[16, 16]} className="align-items-center">
                        <Col xs={24} md={6}>
                          <AntInput
                            placeholder="App name, code"
                            value={filterName || ""}
                            onChange={(e) => handleChangeName(e.target.value)}
                          />
                        </Col>
                        <Col xs={12} md={3}>
                          <AntSelect
                            value={filterOs || undefined}
                            onChange={handleChangeOs}
                            allowClear
                            placeholder="All Os"
                            style={{ width: '100%' }}
                            options={[
                              { 
                                value: 'android', 
                                label: (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <AndroidFilled /> Android
                                  </span>
                                )
                              },
                              { 
                                value: 'ios', 
                                label: (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <AppleFilled /> iOS
                                  </span>
                                )
                              }
                            ]}
                          />
                        </Col>
                        <Col xs={24} md={8} className="sort-container">
                          <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem' }}>
                            <span className="me-3 sort-label">Sort by</span>
                            <Radio.Group
                              value={sortBy}
                              onChange={(e) => handleSortChange(e.target.value)}
                              style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
                            >
                              <Radio value="name">Name</Radio>
                              <Radio value="default">Revenue</Radio>
                              <Radio value="release_at">Release</Radio>
                            </Radio.Group>
                          </div>
                        </Col>
                        <Col xs={24} md={7} className="status-container">
                          <div className="d-flex status-filters align-items-center">
                            <div
                              className={`me-4 lh-lg ${
                                filterStatus === ""
                                  ? "title-block-active"
                                  : "title-block"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleChangeStatus("")}
                            >
                              All ({initialCounts.all ?? 0})
                            </div>
                            <div
                              className={`me-4 lh-lg ${
                                filterStatus === "running"
                                  ? "title-block-active"
                                  : "title-block"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleChangeStatus("running")}
                            >
                              Running ({initialCounts.running ?? 0})
                            </div>
                            <div
                              className={`lh-lg ${
                                filterStatus === "off"
                                  ? "title-block-active"
                                  : "title-block"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleChangeStatus("off")}
                            >
                              Off ({initialCounts.off ?? 0})
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <Flipper 
                    flipKey={`${sortBy}-${sortDirection}-${appFilter.length}`}
                    spring={{
                      type: "spring",
                      stiffness: 300,
                      damping: 25
                    }}
                    staggerConfig={{
                      default: {
                        reverse: false,
                        speed: 0.8
                      }
                    }}
                  >
                    {appFilter.length > 0 ? (
                      <div className="list_app" style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                        gap: '1rem',
                        justifyContent: 'center'
                      }}>
                        {appFilter.map((i, k) => {
                          const uniqueKey = `app-${i.id || i.code}-${i.os}`;
                          return (
                            <Flipped 
                              key={uniqueKey} 
                              flipId={uniqueKey}
                              stagger={true}
                              transformOrigin="center center"
                            >
                              <div>
                                <div className="card">
                                  <div
                                    className={`card-body d-flex align-items-center ${
                                      i.status === "off" ? "app-off" : ""
                                    }`}
                                    style={{ padding: "1.25rem" }}
                                  >
                                    <div className="app-img me-3">
                                      <img
                                        src={i.thumb}
                                        alt={i.name}
                                        onClick={() => selectApp(i)}
                                      />
                                      <img
                                        alt={``}
                                        className="app-logo"
                                        src={`/bg-${i.os}.png`}
                                        onClick={() => selectApp(i)}
                                      />
                                    </div>
                                    <div className="app-title">
                                      <h6
                                        style={{ color: "black" }}
                                        onClick={() => selectApp(i)}
                                      >
                                        {i.code} - {i.name}
                                      </h6>
                                      Status:{" "}
                                      <span
                                        className="mr-2 text-uppercase"
                                        style={{ color: "#29A1DD" }}
                                      >
                                        {i.status}
                                      </span>
                                      OB:{" "}
                                      <span style={{ color: "#29A1DD" }}>
                                        {i.release_at}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Flipped>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="col-12 text-center py-5">
                        <NoDataComponent />
                      </div>
                    )}
                  </Flipper>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Mobile Drawer for filters */}
      <Drawer
        title="Search Filters"
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={320}
        zIndex={2500}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <AntInput
            placeholder="App name, code"
            value={filterName || ''}
            onChange={(e) => setFilterName(e.target.value)}
          />
          <AntSelect
            placeholder="Sort by"
            value={sortBy}
            onChange={(val) => handleSortChange(val)}
            options={[
              { value: 'default', label: 'Revenue' },
              { value: 'name', label: 'Name' },
              { value: 'release_at', label: 'Release' },
            ]}
          />
          <AntSelect
            allowClear
            placeholder="All OS"
            value={filterOs || undefined}
            onChange={(val) => setFilterOs(val || '')}
            options={[
              { 
                value: 'android', 
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AndroidFilled /> Android
                  </span>
                )
              },
              { 
                value: 'ios', 
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AppleFilled /> iOS
                  </span>
                )
              }
            ]}
          />
          <AntSelect
            allowClear
            placeholder="All Status"
            value={filterStatus || undefined}
            onChange={(val) => setFilterStatus(val || '')}
            options={[
              { value: '', label: 'All' },
              { value: 'running', label: 'Running' },
              { value: 'off', label: 'Off' }
            ]}
          />
        </div>
      </Drawer>

    </>
  );
}

export default Apps;